import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../theme';
import { useSupabase } from '../../contexts/SupabaseContext';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';
import { useTheme } from '@mui/material/styles';
import AmountField from '../../components/AmountField';
// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

const Crypto = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-crypto', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [cryptoTransactions, setCryptoTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');
  
  // Form state
  const [cryptoName, setCryptoName] = useState('');
  const [cryptoTicker, setCryptoTicker] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [price, setPrice] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');
  
  // User's saved crypto names (will be populated from existing investments)
  const [userCryptoNames, setUserCryptoNames] = useState([]);

  // Some common cryptocurrency tickers for suggestions
  const commonCryptos = [
    { name: 'Bitcoin', ticker: 'BTC' },
    { name: 'Ethereum', ticker: 'ETH' },
    { name: 'Binance Coin', ticker: 'BNB' },
    { name: 'Solana', ticker: 'SOL' },
    { name: 'Cardano', ticker: 'ADA' },
    { name: 'XRP', ticker: 'XRP' },
    { name: 'Polkadot', ticker: 'DOT' },
    { name: 'Avalanche', ticker: 'AVAX' },
  ];

  // Group investments by crypto name
  const groupInvestmentsByCrypto = (data) => {
    const groupedMap = data.reduce((acc, investment) => {
      if (!acc[investment.name]) {
        acc[investment.name] = {
          name: investment.name,
          ticker: investment.ticker || '',
          totalValue: 0,
          totalUnits: 0,
          transactions: []
        };
      }
      
      acc[investment.name].totalValue += investment.value;
      acc[investment.name].totalUnits += parseFloat(investment.units || 0);
      acc[investment.name].transactions.push(investment);
      
      return acc;
    }, {});
    
    // Convert the grouped map to an array
    return Object.values(groupedMap).map((crypto, index) => ({
      id: index + 1,
      name: crypto.name,
      ticker: crypto.ticker,
      value: crypto.totalValue,
      units: crypto.totalUnits,
      transactions: crypto.transactions
    }));
  };
  
  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching crypto investments for user ID:', user.id);
      const { data, error } = await supabase
        .from('crypto_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      console.log('Fetched crypto data:', data);
      
      if (data && data.length > 0) {
        // Transform the data to match the expected format for the component
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          ticker: item.ticker,
          value: item.amount_invested,
          price: item.price,
          purchaseDate: item.purchase_date,
          units: item.units
        }));
        
        // Group investments by crypto name
        const groupedInvestments = groupInvestmentsByCrypto(formattedData);
        
        setInvestments(groupedInvestments);
        const total = formattedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        console.log('Total Crypto value:', total);
        
        // Extract unique crypto names from user's investments
        const uniqueCryptoNames = [...new Set(data.map(item => item.name))];
        setUserCryptoNames(uniqueCryptoNames);
      } else {
        console.log('No crypto investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserCryptoNames([]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      // Fallback to empty data in case of error
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  // Initial data fetch and connection test
  useEffect(() => {
    const init = async () => {
      try {
        // Test Supabase connection
        console.log('Testing Supabase connection...');
        const { data: connectionTest, error: connectionError } = await supabase
          .from('crypto_investments')
          .select('count')
          .limit(1);
          
        if (connectionError) {
          console.error('Supabase connection error:', connectionError);
        } else {
          console.log('Supabase connection successful:', connectionTest);
        }
        
        // Fetch investments
        await fetchInvestments();
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      }
    };
    
    init();
  }, [fetchInvestments]);

  // Calculate units when price or amount changes
  useEffect(() => {
    if (price && amountInvested && parseFloat(price) > 0 && parseFloat(amountInvested) > 0) {
      setUnits((parseFloat(amountInvested) / parseFloat(price)).toFixed(8));
    } else {
      setUnits('');
    }
  }, [price, amountInvested]);

  // Calculate units when price or amount changes for edit modal
  useEffect(() => {
    if (editPrice && editAmountInvested && parseFloat(editPrice) > 0 && parseFloat(editAmountInvested) > 0) {
      setEditUnits((parseFloat(editAmountInvested) / parseFloat(editPrice)).toFixed(8));
    } else {
      setEditUnits('');
    }
  }, [editPrice, editAmountInvested]);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleReset = () => {
    setCryptoName('');
    setCryptoTicker('');
    setPurchaseDate(new Date());
    setPrice('');
    setUnits('');
    setAmountInvested('');
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!cryptoName || !purchaseDate || !price || !amountInvested) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the crypto name - handle both string input and selection from dropdown
      const cryptoNameValue = typeof cryptoName === 'string' ? cryptoName.trim() : cryptoName;
      
      if (!cryptoNameValue) {
        alert('Please enter a valid cryptocurrency name');
        setLoading(false);
        return;
      }
      
      // Prepare the data for Supabase
      const newInvestment = {
        name: cryptoNameValue,
        ticker: null, // Set ticker explicitly to null
        purchase_date: purchaseDate.toISOString().split('T')[0],
        price: parseFloat(price),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        user_id: user.id
      };

      console.log('Creating new investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('crypto_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransactionHistory = (crypto) => {
    setSelectedCrypto(crypto);
    setCryptoTransactions(crypto.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedCrypto(null);
    setCryptoTransactions([]);
  };

  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditName(currentTransaction.name || '');
    setEditPrice(currentTransaction.price ? currentTransaction.price.toString() : '');
    setEditAmountInvested(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    // Units will be calculated automatically
    
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editName || !editPrice || !editPurchaseDate || !editAmountInvested) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Get the crypto name - handle both string input and selection from dropdown
      const editNameValue = typeof editName === 'string' ? editName.trim() : editName;
      
      if (!editNameValue) {
        alert('Please enter a valid cryptocurrency name');
        setLoading(false);
        return;
      }
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: editNameValue,
        ticker: null, // Set ticker explicitly to null
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        price: parseFloat(editPrice),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('crypto_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedCrypto && openHistoryModal) {
        // Get the latest transactions for this crypto
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('crypto_investments')
          .select('*')
          .eq('name', selectedCrypto.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            ticker: item.ticker,
            value: item.amount_invested,
            price: item.price,
            purchaseDate: item.purchase_date,
            units: item.units
          }));
          
          setCryptoTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('crypto_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedCrypto && openHistoryModal) {
        // Get the latest transactions for this crypto
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('crypto_investments')
          .select('*')
          .eq('name', selectedCrypto.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              ticker: item.ticker,
              value: item.amount_invested,
              price: item.price,
              purchaseDate: item.purchase_date,
              units: item.units
            }));
            
            setCryptoTransactions(formattedTransactions);
          } else {
            // No transactions left for this crypto, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/investments')}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box data-tour="inv-prod-summary">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Cryptocurrency
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <TourButton onClick={tour.start} />
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={fetchInvestments} 
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button 
              data-tour="inv-prod-add"
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 2,
              }}
            >
              Add Investment
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : investments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">No Cryptocurrencies Found</Typography>
            <Typography variant="body2" sx={{ mb: 3, maxWidth: 400 }}>
              No cryptocurrency investments found. Start investing to see them here.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Add Your First Investment
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper
                data-tour="inv-prod-holdings"
                sx={{
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: 'var(--fx-card-shadow)',
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Cryptocurrency Details
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          onClick={() => handleSort('name')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                          }}
                        >
                          Crypto Name
                          {sortConfig.key === 'name' && (
                            <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell 
                          align="right"
                          onClick={() => handleSort('value')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                          }}
                        >
                          Value
                          {sortConfig.key === 'value' && (
                            <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell 
                          align="right"
                          onClick={() => handleSort('allocation')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                          }}
                        >
                          Allocation
                          {sortConfig.key === 'allocation' && (
                            <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {investments.map((investment, index) => (
                        <TableRow
                          key={investment.id}
                          onClick={() => handleOpenTransactionHistory(investment)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: COLORS[index % COLORS.length],
                                  mr: 2,
                                }}
                              />
                              {investment.name} {investment.ticker && `(${investment.ticker})`}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(investment.value)}
                          </TableCell>
                          <TableCell align="right">
                            {((investment.value / totalValue) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View transactions">
                              <IconButton size="small" sx={{ color: 'primary.main' }}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(totalValue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          100%
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4} data-tour="inv-prod-charts">
              <Paper
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: 'var(--fx-card-shadow)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Distribution
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                  {pieChartData.length > 0 && (
                    <PieChart colors={chartPalette}
                      series={[
                        {
                          data: pieChartData,
                          highlightScope: { faded: 'global', highlighted: 'item' },
                          faded: { innerRadius: 30, additionalRadius: -30 },
                        },
                      ]}
                      height={250}
                      legend={{
                        hidden: true
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Modal for adding new investment */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="add-investment-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Add New Cryptocurrency Investment
            </Typography>
            
            <Stack spacing={3}>
              <Autocomplete
                freeSolo
                options={userCryptoNames}
                value={cryptoName}
                onChange={(event, newValue) => setCryptoName(newValue)}
                onInputChange={(event, newInputValue) => {
                  if (event) setCryptoName(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cryptocurrency Name"
                    required
                    fullWidth
                    placeholder="Enter or select cryptocurrency name"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Price per unit at time of purchase"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Total amount invested in this transaction"
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={units}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount Invested ÷ Price)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Transaction Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleTransactionMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150, borderRadius: 2 }
          }}
        >
          <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" color="primary" />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
            <DeleteIcon fontSize="small" color="error" />
            Delete
          </MenuItem>
        </Menu>

        {/* Edit Transaction Modal */}
        <Modal
          open={openEditModal}
          onClose={handleEditModalClose}
          aria-labelledby="edit-transaction-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Edit Transaction
            </Typography>
            
            <Stack spacing={3}>
              <Autocomplete
                freeSolo
                options={userCryptoNames}
                value={editName}
                onChange={(event, newValue) => setEditName(newValue)}
                onInputChange={(event, newInputValue) => {
                  if (event) setEditName(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cryptocurrency Name"
                    required
                    fullWidth
                    placeholder="Enter or select cryptocurrency name"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Price per unit at time of purchase"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Total amount invested in this transaction"
                sx={numberInputStyle}

              />
              
              <TextField
                label="Number of Units"
                value={editUnits}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount Invested ÷ Price)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </Box>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteDialogClose}
          aria-labelledby="delete-transaction-dialog"
        >
          <DialogTitle id="alert-dialog-title">
            Confirm Delete Transaction
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogContentText>
            {currentTransaction && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Details:
                </Typography>
                <Typography variant="body2">
                  Cryptocurrency: {currentTransaction.name} {currentTransaction.ticker && `(${currentTransaction.ticker})`}<br />
                  Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                  Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(currentTransaction.value)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleDeleteDialogClose} 
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteTransaction} 
              variant="contained" 
              color="error"
              sx={{ borderRadius: 2, textTransform: 'none' }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transaction History Modal */}
        <Modal
          open={openHistoryModal}
          onClose={handleCloseHistoryModal}
          aria-labelledby="transaction-history-modal"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  {selectedCrypto?.name} {selectedCrypto?.ticker && `(${selectedCrypto.ticker})`}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Invested Amount: {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(selectedCrypto?.value || 0)} |
                  Total Units: {(selectedCrypto?.units || 0).toFixed(8)} |
                  Average Price: ₹{(() => {
                    if (!cryptoTransactions || cryptoTransactions.length === 0) return '0.00';
                    const totalAmount = cryptoTransactions.reduce((sum, tx) => sum + tx.value, 0);
                    const totalUnits = cryptoTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                    return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                  })()}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseHistoryModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Chip 
                icon={<ReceiptIcon fontSize="small" />} 
                label="Transaction History" 
                color="primary" 
                variant="outlined"
                sx={{ borderRadius: 1 }}
              />
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Price (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Units</TableCell>
                    <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cryptoTransactions.length > 0 ? (
                    cryptoTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {transaction.purchaseDate 
                            ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.price 
                            ? transaction.price.toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(transaction.value)}
                        </TableCell>
                        <TableCell align="right">
                          {transaction.units 
                            ? parseFloat(transaction.units).toFixed(8)
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No transaction history available
                      </TableCell>
                    </TableRow>
                  )}
                  {cryptoTransactions.length > 0 && (
                    <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(cryptoTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {cryptoTransactions
                          .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                          .toFixed(8)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                onClick={handleCloseHistoryModal}
                variant="outlined"
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
        <GuidedTour steps={TOURS['inv-crypto']} open={tour.open} onClose={tour.close} />
      </Box>
    </LocalizationProvider>
  );
};

export default Crypto;